import { AfterViewInit, Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { VideoPlayerPageComponent } from '../../video-player-page/video-player-page.component';
import { VdPlayerControlsComponent } from './vd-player-controls.component';
import { videoImage } from '../video-detail-thumb';
import { PlayState } from '../video-detail-inline';

const AD_STATE_POLL_MS = 300;

/*
 * Inline player for the new video detail page.
 * Extends the album-video page component, so the Stormcloud player (IMA / VMAP ads, licence key),
 * the hls.js fallback, the end-session call and PubNub comments are the existing code.
 * Differences: the video comes from the `payload` input (set by the page after the classic play checks),
 * never from the saved stream info, and the session is ended when the video changes or the page closes.
 */
@Component({
  selector: 'app-vd-player',
  templateUrl: './vd-player.component.html',
  styleUrls: ['./vd-player.component.scss'],
})
export class VdPlayerComponent extends VideoPlayerPageComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() payload: any;
  @Input() theatre = false;
  @Output() ended = new EventEmitter<void>();
  @Output() playState = new EventEmitter<PlayState>();
  @Output() theatreToggle = new EventEmitter<void>();
  @Output() ready = new EventEmitter<VdPlayerComponent>();
  @ViewChild(VdPlayerControlsComponent) controlsRef?: VdPlayerControlsComponent;

  /** True while Stormcloud / IMA plays an ad: our controls hide so the ad keeps its own UI and click-through. */
  adPlaying = false;
  /** Until the video can play (and while it buffers): shows a spinner over the poster. */
  loading = true;

  protected listensToSavedStreamInfo = false;
  private readonly ngZone = inject(NgZone);
  private viewReady = false;
  private commentsChannel = '';
  private adPoll: any;
  private readonly onFullscreenChange = () => this.player?.resize?.();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.payload && this.viewReady) {
      this.load(!!changes.payload.previousValue);
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.payload) this.load(false);
    this.ngZone.runOutsideAngular(() => {
      this.adPoll = setInterval(() => this.checkAdState(), AD_STATE_POLL_MS);
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
    });
    Promise.resolve().then(() => this.ready.emit(this));
  }

  /** Comments use one channel per album; subscribe once, not on every video switch. */
  subscribeToComments(): void {
    if (this.commentsChannel === this.channelURL) return;
    this.commentsChannel = this.channelURL;
    super.subscribeToComments();
  }

  /** Shown while the video loads; without an uploaded image the video's own first frame appears. */
  get posterImage(): string | null {
    return videoImage(this.selectedVideo);
  }

  onKey(event: KeyboardEvent): void {
    if (!this.adPlaying) this.controlsRef?.onKey(event);
  }

  onVideoClick(): void {
    if (!this.adPlaying) this.controlsRef?.togglePlay();
  }

  ngOnDestroy(): void {
    clearInterval(this.adPoll);
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    if (this.payload) this.endSessionWithStreamMetrics();
    this.destroyStormcloud();
    super.ngOnDestroy();
  }

  private load(hadPrevious: boolean): void {
    this.loading = true;
    if (hadPrevious) this.endSessionWithStreamMetrics();
    if (this.payload) this.applyStreamInfo(this.payload);
  }

  private checkAdState(): void {
    const isAd = !!(this.player && typeof this.player.isAdPlaying === 'function' && this.player.isAdPlaying());
    const video = this.videoElement?.nativeElement;
    // Our control bar replaces the native one outside ads.
    if (!isAd && video?.controls) video.controls = false;
    if (isAd !== this.adPlaying) this.ngZone.run(() => { this.adPlaying = isAd; });
  }

  private destroyStormcloud(): void {
    try {
      this.player?.destroy?.();
    } catch (error) {
      console.warn('[VdPlayer] Stormcloud destroy failed', error);
    }
    this.player = null;
  }
}
